/* Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5 · macrostructure: Index-First · F3 knobs: rows=tonal, columns=5, numbers=tabular
 * tone: operational-utilitarian · anchor hue: heritage-green · theme: Operational Architect · nav: N3 side-rail · enrichment: none
 * contrast: pass (40–41) · slop: pass (42–45) · responsive: pass (34, 49–57) · icons: pass (30)
 */
import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  PauseCircle,
  RefreshCw,
  Search,
  UsersRound,
} from 'lucide-react';
import { StatusChip, ProgressBar, Button } from '@lms/ui';
import { useEnrollmentPage } from '../hooks/useEnrollments';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type { EnrollmentStatus } from '@lms/shared/types';

type EnrollmentFilter = 'ALL' | EnrollmentStatus;

const statusOrder: EnrollmentFilter[] = ['ALL', 'ACTIVE', 'PAUSED', 'COMPLETED', 'DROPPED'];

const statusCopy = {
  ACTIVE: copy.participants.activeStatus,
  PAUSED: copy.participants.pausedStatus,
  COMPLETED: copy.participants.completedStatus,
  DROPPED: copy.participants.droppedStatus,
} satisfies Record<EnrollmentStatus, typeof copy.participants.activeStatus>;

export function Participants() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<EnrollmentFilter>('ALL');
  const deferredSearch = useDeferredValue(search);
  const { language, t } = useLanguage();
  const { data, isLoading, isFetching, error, refetch } = useEnrollmentPage({
    page,
    limit: 50,
    search: deferredSearch.trim() || undefined,
    status: status === 'ALL' ? undefined : status,
  });
  const filteredEnrollments = data?.items ?? [];
  const statusCounts = data?.summary.statusCounts;
  const currentRange = (() => {
    if (!data?.meta.total || filteredEnrollments.length === 0) return null;
    const from = (data.meta.page - 1) * data.meta.limit + 1;
    return { from, to: from + filteredEnrollments.length - 1, total: data.meta.total };
  })();
  const attentionCount = filteredEnrollments.filter(
    enrollment => enrollment.status === 'ACTIVE' && enrollment.progress < 50
  ).length;
  const totalMatching = data?.meta.total ?? 0;
  const activeMatching = statusCounts?.ACTIVE ?? 0;

  const selectStatus = (nextStatus: EnrollmentFilter) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setStatus('ALL');
    setPage(1);
  };

  const getStatusLabel = (enrollmentStatus: EnrollmentStatus) => t(statusCopy[enrollmentStatus]);

  return (
    <div className="space-y-8 pb-12 animate-fadeIn">
      <header className="grid gap-6 rounded-3xl bg-surface-container-low p-6 sm:p-8 xl:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)] xl:items-end">
        <div className="min-w-0">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.15em] text-primary">
            <Bilingual {...copy.participants.directoryLabel} />
          </p>
          <h1 className="mt-3 max-w-3xl break-words wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...copy.participants.listTitle} />
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-on-surface-variant sm:text-base">
            <Bilingual {...copy.participants.listSubtitle} />
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-surface-container-lowest px-3 py-2 text-xs font-label font-bold text-on-surface">
            <UsersRound size={15} className="shrink-0 text-primary" />
            <span>{totalMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}</span>
            <span className="text-on-surface-variant">
              <Bilingual {...copy.participants.totalEnrollments} />
            </span>
          </div>
        </div>

        <section
          className="rounded-2xl bg-primary p-5 text-on-primary shadow-level1"
          aria-label={t(copy.participants.directoryLabel)}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.15em] text-on-primary/70">
                <Bilingual {...copy.participants.averageProgress} />
              </p>
              <p className="mt-3 font-headline text-4xl font-extrabold tabular-nums">
                {data?.summary.averageProgress ?? 0}%
              </p>
              <p className="mt-2 text-xs leading-relaxed text-on-primary/80">
                <Bilingual {...copy.participants.activeEnrollments} />:{' '}
                {activeMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="grid size-10 shrink-0 place-items-center rounded-xl bg-surface-container-lowest text-primary transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-on-primary disabled:cursor-not-allowed disabled:opacity-50 active:scale-95"
              aria-label={t(isFetching ? copy.participants.refreshing : copy.participants.refresh)}
              title={t(isFetching ? copy.participants.refreshing : copy.participants.refresh)}
            >
              <RefreshCw size={17} className={isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </section>
      </header>

      <section
        className="rounded-3xl bg-surface-container-low p-5 sm:p-6"
        aria-labelledby="participant-filter-title"
      >
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="w-full max-w-xl">
            <label
              id="participant-filter-title"
              htmlFor="participant-search"
              className="block text-xs font-label font-bold text-on-surface"
            >
              <Bilingual {...copy.participants.searchLabel} />
            </label>
            <div className="relative mt-2">
              <Search
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                size={18}
              />
              <input
                id="participant-search"
                type="search"
                placeholder={
                  language === 'ar'
                    ? copy.participants.searchPlaceholder.ar
                    : copy.participants.searchPlaceholder.id
                }
                className="h-12 w-full rounded-xl bg-surface-container-lowest py-3 pl-12 pr-4 text-sm text-on-surface outline-none transition-colors placeholder:text-on-surface-variant/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <div className="min-w-0 xl:max-w-3xl">
            <p className="m-0 text-xs font-label font-bold text-on-surface">
              <Bilingual {...copy.participants.filterLabel} />
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {statusOrder.map(filterStatus => {
                const isSelected = status === filterStatus;
                const count =
                  filterStatus === 'ALL' ? totalMatching : (statusCounts?.[filterStatus] ?? 0);
                const label =
                  filterStatus === 'ALL' ? copy.participants.allStatuses : statusCopy[filterStatus];
                return (
                  <button
                    key={filterStatus}
                    type="button"
                    onClick={() => selectStatus(filterStatus)}
                    aria-pressed={isSelected}
                    className={`inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-xs font-label font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 ${
                      isSelected
                        ? 'bg-primary text-on-primary'
                        : 'bg-surface-container-lowest text-on-surface hover:bg-surface-container-high'
                    }`}
                  >
                    <Bilingual {...label} />
                    <span
                      className={`tabular-nums ${isSelected ? 'text-on-primary/75' : 'text-on-surface-variant'}`}
                    >
                      {count.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        aria-label={t(copy.participants.directoryLabel)}
      >
        <article className="rounded-2xl bg-surface-container-lowest p-5 shadow-level1">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            <Bilingual {...copy.participants.totalEnrollments} />
          </p>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums text-on-surface">
            {totalMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
          </p>
        </article>
        <article className="rounded-2xl bg-surface-container-lowest p-5 shadow-level1">
          <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
            <Bilingual {...copy.participants.averageProgress} />
          </p>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums text-primary">
            {data?.summary.averageProgress ?? 0}%
          </p>
        </article>
        <article className="rounded-2xl bg-success-container p-5 text-on-success-container">
          <div className="flex items-start justify-between gap-3">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
              <Bilingual {...copy.participants.activeEnrollments} />
            </p>
            <CheckCircle2 size={17} />
          </div>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums">
            {activeMatching.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
          </p>
        </article>
        <article className="rounded-2xl bg-error-container p-5 text-on-error-container">
          <div className="flex items-start justify-between gap-3">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
              <Bilingual {...copy.participants.attentionOnPage} />
            </p>
            <AlertCircle size={17} />
          </div>
          <p className="mt-3 font-headline text-3xl font-extrabold tabular-nums">
            {attentionCount.toLocaleString(language === 'ar' ? 'ar-EG' : 'id-ID')}
          </p>
          <p className="mt-2 text-xs">
            <Bilingual {...copy.participants.attentionDescription} />
          </p>
        </article>
      </section>

      <section
        className="rounded-3xl bg-surface-container-low p-4 shadow-level1 sm:p-5"
        aria-labelledby="participant-directory-title"
      >
        <div className="flex flex-col gap-3 px-1 pb-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2
              id="participant-directory-title"
              className="m-0 font-headline text-xl font-extrabold text-on-surface"
            >
              <Bilingual {...copy.participants.directoryTitle} />
            </h2>
            {currentRange ? (
              <p className="mt-1 text-xs text-on-surface-variant">
                {t(copy.participants.resultRange)
                  .replace('{from}', String(currentRange.from))
                  .replace('{to}', String(currentRange.to))
                  .replace('{total}', String(currentRange.total))}
              </p>
            ) : null}
          </div>
          {isFetching && !isLoading ? (
            <span className="inline-flex items-center gap-2 text-xs font-label font-bold text-primary">
              <Loader2 size={14} className="animate-spin" />
              <Bilingual {...copy.participants.refreshing} />
            </span>
          ) : null}
        </div>
        {isLoading ? (
          <div className="grid gap-3" aria-busy="true">
            {[1, 2, 3, 4, 5].map(item => (
              <div
                key={item}
                className="h-24 animate-shimmer rounded-2xl bg-surface-container-lowest"
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-error-container px-6 py-12 text-center text-on-error-container">
            <AlertCircle size={28} className="mx-auto" />
            <p className="mt-3 text-sm font-body">
              <Bilingual {...copy.participants.failedLoad} />
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void refetch()}
              className="mt-4 bg-surface-container-lowest text-on-surface hover:bg-surface-container-high"
            >
              <Bilingual {...copy.participants.retry} />
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid md:grid-cols-[minmax(0,1.25fr)_minmax(0,1.15fr)_minmax(11rem,1fr)_minmax(8rem,0.7fr)_2.5rem] md:gap-5 rounded-2xl bg-surface-container-high px-6 py-3">
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderParticipant} />
              </div>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderActiveProgram} />
              </div>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderProgressPath} />
              </div>
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                <Bilingual {...copy.participants.tableHeaderCurrentStatus} />
              </div>
              <div />
            </div>

            <div className="mt-3 space-y-3">
              {filteredEnrollments.map(enrollment => (
                <Link
                  key={enrollment.id}
                  to={`/participants/${enrollment.id}`}
                  aria-label={`${t(copy.participants.openDossier)}: ${enrollment.user?.name ?? enrollment.user?.email ?? ''}`}
                  className="group flex flex-col items-start gap-4 rounded-2xl bg-surface-container-lowest p-5 transition-colors duration-150 hover:bg-surface-container-high md:grid md:grid-cols-[minmax(0,1.25fr)_minmax(0,1.15fr)_minmax(11rem,1fr)_minmax(8rem,0.7fr)_2.5rem] md:items-center md:gap-5 md:p-5"
                >
                  {/* Participant Name */}
                  <div className="w-full flex justify-between items-start md:block">
                    <div>
                      <div className="text-sm font-body font-bold text-on-surface group-hover:text-primary transition-colors">
                        {enrollment.user?.name}
                      </div>
                      <div className="text-[10px] font-mono text-on-surface-variant opacity-60">
                        {enrollment.user?.email}
                      </div>
                    </div>
                    <ChevronRight size={18} className="md:hidden shrink-0 text-primary" />
                  </div>

                  {/* Active Program */}
                  <div className="flex items-center justify-between w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                      <Bilingual {...copy.participants.tableHeaderActiveProgram} />
                    </span>
                    <div className="text-sm font-body text-on-surface font-medium max-w-[150px] sm:max-w-[200px] md:max-w-none text-right md:text-left">
                      {enrollment.program?.name}
                    </div>
                  </div>

                  {/* Progress Path */}
                  <div className="flex items-center justify-between w-full md:w-auto gap-4">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                      <Bilingual {...copy.participants.tableHeaderProgressPath} />
                    </span>
                    <div className="flex items-center gap-4 flex-1 md:pr-4 justify-end md:justify-start">
                      <div className="w-24 md:flex-1 hidden sm:block">
                        <ProgressBar value={enrollment.progress} size="sm" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-on-surface mb-1">
                          {Math.round(enrollment.progress)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Current Status */}
                  <div className="flex items-center justify-between w-full md:w-auto">
                    <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                      <Bilingual {...copy.participants.tableHeaderCurrentStatus} />
                    </span>
                    <StatusChip
                      label={getStatusLabel(enrollment.status)}
                      variant={enrollment.status === 'ACTIVE' ? 'success' : 'neutral'}
                    />
                  </div>

                  {/* Actions (Desktop) */}
                  <div className="hidden md:flex justify-end text-primary">
                    <ChevronRight
                      size={18}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </div>
                </Link>
              ))}
            </div>

            {filteredEnrollments.length === 0 && (
              <div className="rounded-2xl bg-surface-container-lowest px-8 py-16 text-center text-sm text-on-surface-variant">
                <PauseCircle size={28} className="mx-auto text-primary/50" />
                <p className="mt-3">
                  <Bilingual {...copy.participants.emptyState} />
                </p>
                {(search || status !== 'ALL') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="mt-3 bg-surface-container-high text-on-surface hover:bg-surface-container-highest"
                  >
                    <Bilingual {...copy.participants.resetFilters} />
                  </Button>
                )}
              </div>
            )}

            {data?.meta && data.meta.totalPages > 1 ? (
              <nav
                className="mt-5 flex items-center justify-between gap-3 rounded-2xl bg-surface-container-lowest px-4 py-3"
                aria-label={t(copy.participants.directoryTitle)}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(data.meta.page - 1)}
                  disabled={data.meta.page === 1}
                  leftIcon={<ChevronLeft size={16} />}
                  className="whitespace-nowrap bg-surface-container-low text-on-surface hover:bg-surface-container-high disabled:opacity-40"
                >
                  <Bilingual {...copy.common.previous} />
                </Button>
                <span className="text-xs font-body tabular-nums text-on-surface-variant">
                  <strong className="text-on-surface">{data.meta.page}</strong> /{' '}
                  {data.meta.totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(data.meta.page + 1)}
                  disabled={data.meta.page === data.meta.totalPages}
                  rightIcon={<ChevronRight size={16} />}
                  className="whitespace-nowrap bg-surface-container-low text-on-surface hover:bg-surface-container-high disabled:opacity-40"
                >
                  <Bilingual {...copy.common.next} />
                </Button>
              </nav>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
